// Derive the runtime display status for a deliverable. Mirrors the
// semantics of backend `deriveDeliverableStatus` in
// `backend/src/models/tracking.ts` — both produce the same derived
// status for the same logical inputs. Argument order differs from the
// server (`storedStatus, dueDate, today, submittedAt` here vs
// `dueDate, storedStatus, today, submittedAt` on the backend) for FE
// call-site ergonomics; do not assume a positional copy/paste between
// the two callsites is safe. Two distinct derived states overlay the
// stored 8-value DeliverableStatus enum:
//
//   `overdue` — supplier hasn't submitted yet and the due date is past.
//               Action queue: chase supplier. Drives the Overdue tile +
//               Overdue filter chip + Contract overview "Overdue
//               deliverables" card. Only `not-started` flips to overdue;
//               `revision-requested` is its own queue (supplier rework).
//
//   `late`    — supplier submitted (or under review) but the submission
//               arrived after the due date. Action queue: customer
//               review of a delayed delivery. Drives the Late filter
//               chip + amber row pill annotation. Counted toward the
//               Awaiting review tile (customer review queue), NOT the
//               Overdue tile or Overdue card.
//
// Terminal states (accepted/rejected/closed) are sticky — a deliverable
// that was completed late is still "done", not "overdue". Reopening a
// late deliverable will correctly re-surface it as overdue on the next
// render.

import type {
  DeliverableDerivedStatus,
  DeliverableStatus,
} from "../../../../types";

const TERMINAL: ReadonlySet<DeliverableStatus> = new Set([
  "accepted",
  "rejected",
  "closed",
]);

export function deriveDeliverableStatus(
  storedStatus: DeliverableStatus,
  dueDate: string | undefined,
  today: Date = new Date(),
  submittedAt?: string | null,
): DeliverableDerivedStatus {
  if (TERMINAL.has(storedStatus)) return storedStatus;
  if (!dueDate) return storedStatus;
  const todayIso = today.toISOString().slice(0, 10);
  // `late`: submitted or under-review and submission date > due date.
  // Compare the date portion only (first 10 chars of ISO timestamp vs
  // YYYY-MM-DD dueDate).
  if (
    (storedStatus === "submitted" || storedStatus === "under-review") &&
    submittedAt &&
    submittedAt.slice(0, 10) > dueDate
  ) {
    return "late";
  }
  // `overdue`: no submission yet, past due, and still in the open
  // pre-submission state. revision-requested falls through — supplier
  // rework is its own queue.
  if (
    dueDate < todayIso &&
    !submittedAt &&
    storedStatus === "not-started"
  ) {
    return "overdue";
  }
  return storedStatus;
}
